class MeridianHeaderNavigation extends HTMLElement {
  connectedCallback() {
    this._cleanups = [];
    this._headerState = null;
    this._handledEscape = false;
    this._details = Array.from(this.querySelectorAll('details'));
    this._details.forEach((details) => this.bindDetails(details));

    this._onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.closeAll();
    };
    this._onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const openDetails = this._details.find((details) => details.open);
      if (!openDetails) return;
      event.preventDefault();
      event.stopPropagation();
      this._handledEscape = true;
      openDetails.removeAttribute('open');
      openDetails.querySelector('summary')?.focus();
    };
    this._onKeyup = (event) => {
      if (event.key === 'Escape' && this._handledEscape) {
        event.stopPropagation();
        this._handledEscape = false;
      }
    };
    this._onMobileSummaryClick = (event) => {
      if (
        event.target.closest?.('summary')?.parentElement?.matches?.('.meridian-header-nav__group')
      ) {
        event.stopPropagation();
      }
    };

    document.addEventListener('click', this._onDocumentClick);
    this.addEventListener('keydown', this._onKeydown);
    this.addEventListener('keyup', this._onKeyup, true);
    if (this.dataset.mode === 'mobile') this.addEventListener('click', this._onMobileSummaryClick, true);
    this._cleanups.push(() => document.removeEventListener('click', this._onDocumentClick));
    this._cleanups.push(() => this.removeEventListener('keydown', this._onKeydown));
    this._cleanups.push(() => this.removeEventListener('keyup', this._onKeyup, true));
    this._cleanups.push(() => this.removeEventListener('click', this._onMobileSummaryClick, true));
  }

  disconnectedCallback() {
    this.closeAll();
    this.restoreHeaderState();
    this._cleanups?.forEach((cleanup) => cleanup());
    this._cleanups = [];
  }

  bindDetails(details) {
    const onToggle = () => {
      details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
      if (details.open) {
        this._details.forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      }
      this.syncHeaderState();
    };
    details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
    details.addEventListener('toggle', onToggle);
    this._cleanups.push(() => details.removeEventListener('toggle', onToggle));

    if (this.dataset.mode !== 'desktop') return;

    let closeTimer;
    const clearClose = () => window.clearTimeout(closeTimer);
    const canHover = () => window.matchMedia('(hover: hover) and (min-width: 990px)').matches;
    const open = () => {
      if (!canHover()) return;
      clearClose();
      details.setAttribute('open', '');
      this.syncHeaderState();
    };
    const scheduleClose = () => {
      if (!canHover()) return;
      clearClose();
      closeTimer = window.setTimeout(() => {
        if (details.contains(document.activeElement)) return;
        details.removeAttribute('open');
        this.syncHeaderState();
      }, 140);
    };
    const onFocusOut = (event) => {
      if (!details.contains(event.relatedTarget)) scheduleClose();
    };

    details.addEventListener('pointerenter', open);
    details.addEventListener('pointerleave', scheduleClose);
    this._cleanups.push(() => details.removeEventListener('pointerenter', open));
    this._cleanups.push(() => details.removeEventListener('pointerleave', scheduleClose));
    details.addEventListener('focusout', onFocusOut);
    this._cleanups.push(() => details.removeEventListener('focusout', onFocusOut));
    this._cleanups.push(() => clearClose());
  }

  closeAll() {
    this._details?.forEach((details) => details.removeAttribute('open'));
    this.syncHeaderState();
  }

  syncHeaderState() {
    if (this.dataset.mode !== 'desktop') return;
    const hasOpenDetails = this._details?.some((details) => details.open);
    if (hasOpenDetails) {
      if (!this._headerState) {
        const wrapper = this.closest('.header-wrapper');
        if (!wrapper) return;
        const rootStyle = document.documentElement.style;
        this._headerState = {
          wrapper,
          hadPreventHide: Object.prototype.hasOwnProperty.call(wrapper, 'preventHide'),
          previousPreventHide: wrapper.preventHide,
          previousBottomPosition: rootStyle.getPropertyValue('--header-bottom-position-desktop'),
        };
      }

      const { wrapper } = this._headerState;
      const bottom = wrapper.getBoundingClientRect?.().bottom;
      wrapper.preventHide = true;
      if (Number.isFinite(bottom)) {
        document.documentElement.style.setProperty(
          '--header-bottom-position-desktop',
          `${Math.floor(bottom)}px`
        );
      }
      return;
    }

    this.restoreHeaderState();
  }

  restoreHeaderState() {
    if (!this._headerState) return;
    const { wrapper, hadPreventHide, previousPreventHide, previousBottomPosition } = this._headerState;
    if (hadPreventHide) wrapper.preventHide = previousPreventHide;
    else delete wrapper.preventHide;

    const rootStyle = document.documentElement.style;
    if (previousBottomPosition) {
      rootStyle.setProperty('--header-bottom-position-desktop', previousBottomPosition);
    } else {
      rootStyle.removeProperty('--header-bottom-position-desktop');
    }
    this._headerState = null;
  }
}

if (!customElements.get('meridian-header-navigation')) {
  customElements.define('meridian-header-navigation', MeridianHeaderNavigation);
}
